import { Controller } from "@hotwired/stimulus"
import { escapeHtml as escapeHtmlValue } from "../helpers/escape_html"

const SECTION_MAP = {
  ai_conversation_template: "aiSection",
  call_template: "callSection",
  form_template: "formSection",
  document_validation_template: "documentSection",
  ai_analytic_template: "analyticsSection"
}

const ICON_X_CLOSE = '<svg class="w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18M6 6L18 18"/></svg>'
const ICON_TRASH = '<svg class="w-full h-full" viewBox="0 0 24 24" fill="none"><path d="M9 3H15M3 6H21M19 6L18.2987 16.5193C18.1935 18.0975 18.1409 18.8867 17.8 19.485C17.4999 20.0118 17.0472 20.4353 16.5017 20.6997C15.882 21 15.0911 21 13.5093 21H10.4907C8.90891 21 8.11803 21 7.49834 20.6997C6.95276 20.4353 6.50009 20.0118 6.19998 19.485C5.85911 18.8867 5.8065 18.0975 5.70129 16.5193L5 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
const ICON_UPLOAD_CLOUD = '<svg class="w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 16L12 12M12 12L16 16M12 12V21M20 16.7428C21.2215 15.734 22 14.2079 22 12.5C22 9.46243 19.5376 7 16.5 7C16.2815 7 16.0771 6.886 15.9661 6.69774C14.6621 4.48484 12.2544 3 9.5 3C5.35786 3 2 6.35786 2 10.5C2 12.5661 2.83545 14.4371 4.18695 15.7935"/></svg>'

export default class extends Controller {
  static targets = [
    "templateType", "hiddenMetadata", "jsonPreview",
    "aiSection", "callSection", "formSection", "documentSection", "analyticsSection"
  ]

  connect() {
    // Show correct section on load (edit mode)
    this.showActiveSection()
  }

  templateTypeTargetConnected() {
    this.templateTypeTarget.addEventListener("change", () => this.showActiveSection())
  }

  showActiveSection() {
    const type = this.hasTemplateTypeTarget ? this.templateTypeTarget.value : null

    // Hide all sections
    Object.values(SECTION_MAP).forEach(target => {
      if (this[`has${target.charAt(0).toUpperCase() + target.slice(1)}Target`]) {
        this[`${target}Target`].classList.add("hidden")
      }
    })

    // Show matching section
    if (type && SECTION_MAP[type]) {
      const target = SECTION_MAP[type]
      if (this[`has${target.charAt(0).toUpperCase() + target.slice(1)}Target`]) {
        this[`${target}Target`].classList.remove("hidden")
      }
    }

    this.updateJsonPreview()
  }

  // Called before form submit
  collectMetadata(e) {
    const type = this.hasTemplateTypeTarget ? this.templateTypeTarget.value : null
    if (!type || !SECTION_MAP[type]) return

    const targetName = SECTION_MAP[type]
    const section = this[`${targetName}Target`]
    const metadata = { template_type: type }

    // Collect all fields with data-meta-key
    section.querySelectorAll("[data-meta-key]").forEach(el => {
      const key = el.dataset.metaKey
      const value = this._parseValue(el)
      if (value !== null && value !== undefined) {
        this._setNested(metadata, key, value)
      }
    })

    if (this.hasHiddenMetadataTarget) {
      this.hiddenMetadataTarget.value = JSON.stringify(metadata)
    }
  }

  updateJsonPreview() {
    if (!this.hasJsonPreviewTarget) return

    const type = this.hasTemplateTypeTarget ? this.templateTypeTarget.value : null
    if (!type || !SECTION_MAP[type]) {
      this.jsonPreviewTarget.textContent = "{}"
      return
    }

    const targetName = SECTION_MAP[type]
    if (!this[`has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`]) return

    const section = this[`${targetName}Target`]
    const metadata = { template_type: type }

    section.querySelectorAll("[data-meta-key]").forEach(el => {
      const key = el.dataset.metaKey
      const value = this._parseValue(el)
      if (value !== null && value !== undefined) {
        this._setNested(metadata, key, value)
      }
    })

    this.jsonPreviewTarget.textContent = JSON.stringify(metadata, null, 2)
  }

  // === Question Blocks (AI Conversation) ===

  addQuestionBlock() {
    const container = document.getElementById("question-blocks-container")
    if (!container) return

    const index = container.querySelectorAll("[data-block-index]").length
    const html = this._questionBlockHtml(index, "", "")
    container.insertAdjacentHTML("beforeend", html)
    this.updateJsonPreview()
  }

  removeQuestionBlock(e) {
    const block = e.target.closest("[data-block-index]")
    if (block) {
      block.remove()
      this._reindexBlocks()
      this.updateJsonPreview()
    }
  }

  _questionBlockHtml(index, title, questions) {
    return `
      <div class="border border-gray-200 rounded-lg p-4 space-y-3" data-block-index="${index}">
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-gray-700">Block ${index + 1}</label>
          <button type="button" data-action="click->catalog-form-builder#removeQuestionBlock"
                  class="text-xs text-red-600 hover:text-red-800 font-medium">Remove</button>
        </div>
        <input type="text" placeholder="Block title (e.g. Opening Questions)"
               value="${this._escapeHtml(title)}"
               data-block-field="title"
               data-action="input->catalog-form-builder#updateJsonPreview"
               class="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-brand-500" />
        <textarea placeholder="One question per line"
                  rows="3"
                  data-block-field="questions"
                  data-action="input->catalog-form-builder#updateJsonPreview"
                  class="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-brand-500">${this._escapeHtml(questions)}</textarea>
      </div>
    `
  }

  _reindexBlocks() {
    const container = document.getElementById("question-blocks-container")
    if (!container) return
    container.querySelectorAll("[data-block-index]").forEach((block, i) => {
      block.dataset.blockIndex = i
      const label = block.querySelector("label")
      if (label) label.textContent = `Block ${i + 1}`
    })
  }

  // === Form Fields Builder ===

  addFormField() {
    const container = document.getElementById("form-fields-container")
    if (!container) return

    const index = container.querySelectorAll("[data-field-index]").length
    const html = this._formFieldHtml(index, {})
    container.insertAdjacentHTML("beforeend", html)
    this._toggleEmptyState()
    this.updateJsonPreview()
  }

  addFormFieldFromModal(e) {
    const type = e.currentTarget.dataset.fieldType
    if (!type) return

    const container = document.getElementById("form-fields-container")
    if (!container) return

    const index = container.querySelectorAll("[data-field-index]").length
    const html = this._formFieldHtml(index, { type })
    container.insertAdjacentHTML("beforeend", html)
    this._toggleEmptyState()
    this.updateJsonPreview()
  }

  removeFormField(e) {
    const field = e.target.closest("[data-field-index]")
    if (field) {
      field.remove()
      this._reindexFields()
      this._toggleEmptyState()
      this.updateJsonPreview()
    }
  }

  toggleRequired(e) {
    const fieldEl = e.target.closest("[data-field-index]")
    if (!fieldEl) return

    const isRequired = fieldEl.dataset.fieldRequired === "true"
    fieldEl.dataset.fieldRequired = isRequired ? "false" : "true"

    const btn = fieldEl.querySelector("[data-required-toggle]")
    if (btn) {
      if (isRequired) {
        btn.textContent = "Optional"
        btn.className = "px-2 py-0.5 text-xs rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
      } else {
        btn.textContent = "Required"
        btn.className = "px-2 py-0.5 text-xs rounded-full bg-error-50 text-error-600 font-medium transition-colors"
      }
    }
    this.updateJsonPreview()
  }

  addFieldOption(e) {
    const fieldEl = e.target.closest("[data-field-index]")
    if (!fieldEl) return
    const container = fieldEl.querySelector("[data-options-container]")
    if (!container) return

    const addBtn = container.querySelector("[data-add-option]")
    const optionCount = container.querySelectorAll("[data-option-item]").length
    const letter = String.fromCharCode(65 + optionCount)

    const html = `
      <div class="flex items-center gap-2 py-1 pl-3 pr-3 rounded-lg border border-gray-200" data-option-item>
        <span class="w-4 h-4 flex items-center justify-center rounded text-xs font-bold border border-gray-300 text-gray-500" style="font-size: 10px;" data-option-letter>${letter}</span>
        <input type="text" value="" data-option-value
               data-action="input->catalog-form-builder#updateJsonPreview"
               class="flex-1 text-sm border-0 bg-transparent focus:ring-0 px-0 py-0 text-gray-600 placeholder-gray-300"
               placeholder="Option label" />
        <button type="button" data-action="click->catalog-form-builder#removeFieldOption"
                class="p-0.5 text-gray-300 hover:text-error-400 transition-colors">
          <span class="w-3 h-3 block">${ICON_X_CLOSE}</span>
        </button>
      </div>
    `
    addBtn.insertAdjacentHTML("beforebegin", html)
    this.updateJsonPreview()
  }

  removeFieldOption(e) {
    const optionItem = e.target.closest("[data-option-item]")
    const fieldEl = e.target.closest("[data-field-index]")
    if (!optionItem || !fieldEl) return

    optionItem.remove()
    this._reLetterOptions(fieldEl)
    this.updateJsonPreview()
  }

  _formFieldHtml(index, field) {
    const label = field.label || ""
    const type = field.type || "text"
    const required = field.required !== false
    const placeholder = field.placeholder || ""
    const reqClass = required
      ? "bg-error-50 text-error-600 font-medium"
      : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"

    return `
      <div class="flex flex-col gap-4 py-6 group/field" data-field-index="${index}" data-field-required="${required}">
        <input type="hidden" data-field-attr="type" value="${type}" />

        <div class="flex items-start gap-2">
          <div class="flex items-baseline gap-1.5 pt-1 flex-shrink-0">
            <span class="text-base font-medium text-brand-600" data-field-number>${index + 1}</span>
            <span class="text-brand-600">&rarr;</span>
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <input type="text" value="${this._escapeHtml(label)}" data-field-attr="label"
                     data-action="input->catalog-form-builder#updateJsonPreview"
                     class="flex-1 w-full text-xl font-semibold border-0 bg-transparent focus:ring-0 px-0 py-0 text-gray-900 placeholder-gray-300"
                     placeholder="Your question here..." />
            </div>
            <input type="text" value="${this._escapeHtml(placeholder)}" data-field-attr="placeholder"
                   data-action="input->catalog-form-builder#updateJsonPreview"
                   class="w-full text-sm text-gray-500 border-0 bg-transparent focus:ring-0 px-0 py-0 mt-1 placeholder-gray-300"
                   placeholder="Description (optional)" />
          </div>

          <div class="flex items-center gap-1.5 flex-shrink-0 opacity-0 group-hover/field:opacity-100 transition-opacity">
            <button type="button" data-action="click->catalog-form-builder#toggleRequired" data-required-toggle
                    class="px-2 py-0.5 text-xs rounded-full transition-colors ${reqClass}">
              ${required ? "Required" : "Optional"}
            </button>
            <button type="button" data-action="click->catalog-form-builder#removeFormField"
                    class="p-1 text-gray-300 hover:text-error-500 transition-colors" title="Remove field">
              <span class="w-4 h-4 block">${ICON_TRASH}</span>
            </button>
          </div>
        </div>

        <div class="ml-8" data-field-preview>
          ${this._typePreviewHtml(type)}
        </div>
      </div>
    `
  }

  _typePreviewHtml(type) {
    switch (type) {
    case "text":
    case "email":
    case "phone":
    case "url":
    case "number":
      return `<div><input type="text" disabled class="w-full py-1.5 bg-transparent border-0 border-b border-gray-200 text-sm text-gray-400 placeholder-gray-300" placeholder="Type your answer here..." /></div>`

    case "textarea":
      return `<div><textarea disabled rows="2" class="w-full py-1.5 bg-transparent border-0 border-b border-gray-200 text-sm text-gray-400 placeholder-gray-300 resize-none" placeholder="Type your answer here..."></textarea></div>`

    case "select":
    case "multi_select":
    case "ranking":
      return `
          <div class="inline-flex flex-col gap-1.5" data-options-container>
            <button type="button" data-action="click->catalog-form-builder#addFieldOption" data-add-option
                    class="flex items-center gap-2 py-1 pl-3 pr-3 rounded-lg border border-dashed border-gray-200 text-sm text-gray-400 hover:text-brand-600 hover:border-brand-300 transition-colors">
              <span class="w-4 h-4 flex items-center justify-center rounded border border-dashed border-gray-300 text-gray-400" style="font-size: 10px;">+</span>
              Add option
            </button>
          </div>
        `

    case "checkbox":
      return `
          <div class="inline-flex flex-col gap-1.5" data-options-container>
            <div class="flex items-center gap-2">
              <span class="w-4 h-4 flex items-center justify-center rounded border border-gray-300 flex-shrink-0"></span>
              <span class="text-sm text-gray-400">Check to agree</span>
            </div>
            <button type="button" data-action="click->catalog-form-builder#addFieldOption" data-add-option
                    class="flex items-center gap-2 py-1 pl-3 pr-3 rounded-lg border border-dashed border-gray-200 text-sm text-gray-400 hover:text-brand-600 hover:border-brand-300 transition-colors">
              <span class="w-4 h-4 flex items-center justify-center rounded border border-dashed border-gray-300 text-gray-400" style="font-size: 10px;">+</span>
              Add option
            </button>
          </div>
        `

    case "yes_no":
      return `
          <div class="inline-flex flex-col gap-1.5">
            <div class="py-1 pl-3 pr-12 rounded-lg border border-gray-200">
              <span class="inline-flex items-center gap-2">
                <span class="w-4 h-4 flex items-center justify-center rounded text-xs font-bold border border-gray-300 text-gray-500" style="font-size: 10px;">Y</span>
                <span class="text-sm text-gray-600">Yes</span>
              </span>
            </div>
            <div class="py-1 pl-3 pr-12 rounded-lg border border-gray-200">
              <span class="inline-flex items-center gap-2">
                <span class="w-4 h-4 flex items-center justify-center rounded text-xs font-bold border border-gray-300 text-gray-500" style="font-size: 10px;">N</span>
                <span class="text-sm text-gray-600">No</span>
              </span>
            </div>
          </div>
        `

    case "rating":
      return `
          <div class="flex items-center gap-1.5">
            ${[1, 2, 3, 4, 5].map(() => '<span class="w-7 h-7 flex items-center justify-center rounded-lg text-sm text-gray-300 bg-gray-50 border border-gray-200">&#9733;</span>').join("")}
          </div>
        `

    case "opinion_scale":
      return `
          <div class="flex flex-col gap-1">
            <div class="flex gap-1">
              ${Array.from({ length: 10 }, (_, i) => `<span class="flex-1 h-7 flex items-center justify-center rounded-md bg-gray-50 text-xs font-medium text-gray-500 border border-gray-200">${i + 1}</span>`).join("")}
            </div>
            <div class="flex justify-between text-xs text-gray-400 mt-0.5">
              <span>Not likely</span>
              <span>Very likely</span>
            </div>
          </div>
        `

    case "date":
      return `<div><input type="date" disabled class="w-full py-1.5 bg-transparent border-0 border-b border-gray-200 text-sm text-gray-400" /></div>`

    case "file_upload":
      return `
          <div class="border border-dashed border-gray-200 rounded-lg p-5 text-center">
            <div class="flex flex-col items-center gap-1.5">
              <div class="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                <span class="w-4 h-4 text-gray-400 block">${ICON_UPLOAD_CLOUD}</span>
              </div>
              <p class="text-xs text-gray-400">File upload</p>
            </div>
          </div>
        `

    default:
      return ""
    }
  }

  _reindexFields() {
    const container = document.getElementById("form-fields-container")
    if (!container) return
    container.querySelectorAll("[data-field-index]").forEach((field, i) => {
      field.dataset.fieldIndex = i
      const numberEl = field.querySelector("[data-field-number]")
      if (numberEl) numberEl.textContent = i + 1
    })
  }

  _reLetterOptions(fieldEl) {
    const items = fieldEl.querySelectorAll("[data-option-item]")
    items.forEach((item, i) => {
      const letterEl = item.querySelector("[data-option-letter]")
      if (letterEl) letterEl.textContent = String.fromCharCode(65 + i)
    })
  }

  _toggleEmptyState() {
    const container = document.getElementById("form-fields-container")
    const emptyState = document.getElementById("form-fields-empty-state")
    const countEl = document.getElementById("form-fields-count")
    if (!container) return

    const count = container.querySelectorAll("[data-field-index]").length
    if (emptyState) emptyState.classList.toggle("hidden", count > 0)
    if (countEl) countEl.textContent = `${count} item(s)`
  }

  // === Call Outputs ===

  updateCallOutputs() {
    this.updateJsonPreview()
  }

  // === Helpers ===

  _parseValue(el) {
    const metaType = el.dataset.metaType

    if (el.type === "checkbox" && !metaType) {
      return el.checked
    }

    if (metaType === "number") {
      return el.value ? parseFloat(el.value) : 0
    }

    if (metaType === "comma-array") {
      return el.value ? el.value.split(",").map(s => s.trim()).filter(Boolean) : []
    }

    if (metaType === "line-array") {
      return el.value ? el.value.split("\n").map(s => s.trim()).filter(Boolean) : []
    }

    if (metaType === "question-blocks") {
      return this._collectQuestionBlocks()
    }

    if (metaType === "form-fields") {
      return this._collectFormFields()
    }

    if (metaType === "call-outputs") {
      return this._collectCallOutputs()
    }

    return el.value || ""
  }

  _collectQuestionBlocks() {
    const container = document.getElementById("question-blocks-container")
    if (!container) return []

    const blocks = []
    container.querySelectorAll("[data-block-index]").forEach(block => {
      const title = block.querySelector("[data-block-field='title']")?.value || ""
      const questions = (block.querySelector("[data-block-field='questions']")?.value || "")
        .split("\n").map(s => s.trim()).filter(Boolean)
      if (title || questions.length > 0) {
        blocks.push({ title, questions })
      }
    })
    return blocks
  }

  _collectFormFields() {
    const container = document.getElementById("form-fields-container")
    if (!container) return []

    const fields = []
    container.querySelectorAll("[data-field-index]").forEach((fieldEl, i) => {
      const label = fieldEl.querySelector("[data-field-attr='label']")?.value || ""
      const type = fieldEl.querySelector("[data-field-attr='type']")?.value || "text"
      const required = fieldEl.dataset.fieldRequired === "true"
      const placeholder = fieldEl.querySelector("[data-field-attr='placeholder']")?.value || ""

      // Collect options from individual inputs
      const optionEls = fieldEl.querySelectorAll("[data-option-value]")
      const options = Array.from(optionEls).map(el => el.value.trim()).filter(Boolean)

      fields.push({
        id: `field_${i + 1}`,
        type,
        label,
        required,
        placeholder,
        options: ["select", "multi_select", "checkbox", "ranking"].includes(type) ? options : [],
        validation: {}
      })
    })
    return fields
  }

  _collectCallOutputs() {
    const outputs = {}
    document.querySelectorAll("[data-call-output]").forEach(cb => {
      if (cb.checked) {
        outputs[cb.dataset.callOutput] = { status: "pending" }
      }
    })
    return outputs
  }

  _setNested(obj, path, value) {
    const keys = path.split(".")
    let current = obj
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i]
      const nextKey = keys[i + 1]
      const nextIsIndex = /^\d+$/.test(nextKey)

      if (!current[key] || typeof current[key] !== "object") {
        current[key] = nextIsIndex ? [] : {}
      }
      current = current[key]
    }
    const lastKey = keys[keys.length - 1]
    if (/^\d+$/.test(lastKey)) {
      const idx = parseInt(lastKey, 10)
      if (!current[idx] || typeof current[idx] !== "object") {
        current[idx] = value
      } else {
        Object.assign(current[idx], typeof value === "object" ? value : { value })
      }
    } else {
      current[lastKey] = value
    }
  }

  _escapeHtml(str) {
    return escapeHtmlValue(str)
  }
}
